import AlertModal from '@/components/ux/AlertModal/AlertModal';
import { capturePosthogEvent, log, userUtils, utils } from '@/helpers';
import { useCampaignBuilderStore, useCampaignsStore, useCoMarketingInstallerEditStore, useCoMarketingStore } from '@/stores';
import { Button, Flex, FormTextArea } from '@alpine-iq/ui-library';
import { isEqual } from 'lodash';
import React from 'react';
import { createPortal } from 'react-dom';
import { CampaignEditingMethod } from '../campaign.builder.utils';

export default function CampaignActionButtons() {
	const { setActiveStatus } = useCampaignsStore();
	const {
		//
		type,
		sponsoredTemplate,
		installedTemplate,
		ogSponsoredTemplate,
		ogInstalledTemplate,
		setCoMarketingTemplatePublishStatus,
		changesRequireApproval,
		installTemplate,
		requestEdits,
		rejectProposal,
		denyEdits,
		approveEdits,
		cancelEditRequest,
	} = useCoMarketingStore();
	const { campaign, hasChanged, saveCampaign, isDisabled, setCampaign, isCopying, editingMethod } = useCampaignBuilderStore();
	const { installerRequest } = useCoMarketingInstallerEditStore();

	const [installTemplateOpen, setInstallTemplateOpen] = React.useState(false);
	const [launchCampaignOpen, setLaunchCampaignOpen] = React.useState(false);
	const [requestEditsForInstallOpen, setRequestEditsForInstallOpen] = React.useState(false);
	const [deactivateCampaignOpen, setDeactivateCampaignOpen] = React.useState(false);
	const [sendToRetailersOpen, setSendToRetailersOpen] = React.useState(false);
	const [deactivateCoMarketingOpen, setDeactivateCoMarketingOpen] = React.useState(false);
	const [rejectProposalOpen, setRejectProposalOpen] = React.useState(false);
	const [retractRequestOpen, setRetractRequestOpen] = React.useState(false);
	const [requestEditsOpen, setRequestEditsOpen] = React.useState(false);
	const [rejectInstallOpen, setRejectInstallOpen] = React.useState(false);
	const [approveInstallOpen, setApproveInstallOpen] = React.useState(false);
	const [comment, setComment] = React.useState('');

	const isLocked = campaign?.isLocked;
	const canBypassLocked = userUtils.isAdmin() && userUtils.debugMode();
	const isLockedAndCannotBypass = isLocked && !canBypassLocked;

	const requiresApprovalForChanges = React.useMemo(
		() => changesRequireApproval(),
		[campaign, sponsoredTemplate, installedTemplate, ogSponsoredTemplate, ogInstalledTemplate],
	);

	const hasMadeChanges = React.useMemo(() => {
		if (type === 'sponsored') {
			return hasChanged() || !isEqual(sponsoredTemplate, ogSponsoredTemplate);
		}
		return hasChanged() || !isEqual(installedTemplate, ogInstalledTemplate);
	}, [campaign, editingMethod, sponsoredTemplate, installedTemplate, ogSponsoredTemplate, ogInstalledTemplate]);

	const canSaveDraft = (hasMadeChanges || isCopying) && !isDisabled && !isLockedAndCannotBypass;
	const canSavePublish = (hasMadeChanges || isCopying) && !isDisabled && !isLockedAndCannotBypass;

	// Put this in a callback to prevent spamming the console
	React.useEffect(() => {
		log(
			'canSaveDraft',
			canSaveDraft,
			'canSavePublish',
			canSavePublish,
			'hasMadeChanges',
			hasMadeChanges,
			'isCopying',
			isCopying,
			'isDisabled',
			isDisabled,
			'isLockedAndCannotBypass',
			isLockedAndCannotBypass,
		);
	}, [canSaveDraft, canSavePublish, hasMadeChanges, isCopying, isDisabled, isLockedAndCannotBypass]);

	const handleDeactivateCampaign = async () => {
		const success = await setActiveStatus(campaign?.id || '', false);
		if (success) {
			setCampaign({ ...campaign, isActive: false, draft: true });
			setDeactivateCampaignOpen(false);
		}
		return success;
	};

	const handleLaunchCampaign = async () => {
		// Save campaign & set to active status
		setCampaign({ ...campaign, isActive: true, draft: false });

		await saveCampaign();
		await setActiveStatus(campaign?.id || '', true);
		setLaunchCampaignOpen(false);
	};

	const handleSave = async () => {
		await saveCampaign();
		capturePosthogEvent('Campaign Builder - Save Campaign', {
			campaignID: campaign?.id,
			editingMethod,
		});
	};

	const handleSendToRetailers = async () => {
		// Save campaign & co-marketing template
		await saveCampaign();
		// Set the co-marketing template to published
		await setCoMarketingTemplatePublishStatus(true);

		setSendToRetailersOpen(false);

		capturePosthogEvent('Campaign Builder - Send to Retailers', {
			campaignID: campaign?.id,
			editingMethod,
		});
	};

	const handleDeactivateCoMarketing = async () => {
		// Save the campaign & co-marketing template
		await saveCampaign();
		// Set the co-marketing template to unpublished
		await setCoMarketingTemplatePublishStatus(false);
		setDeactivateCoMarketingOpen(false);

		capturePosthogEvent('Campaign Builder - Deactivate Co-Marketing', {
			campaignID: campaign?.id,
			editingMethod,
		});
	};

	const handleRequestEditsForInstall = async () => {
		const success = await requestEdits(comment, true);
		if (success) {
			setRequestEditsForInstallOpen(false);
			setComment('');

			capturePosthogEvent('Campaign Builder - Request Edits for Install', {
				campaignID: campaign?.id,
				editingMethod,
			});
			utils.reload(undefined, 350);
		}
		return success;
	};

	const handleInstallTemplate = async () => {
		const success = await installTemplate(true);
		if (success) {
			setInstallTemplateOpen(false);

			capturePosthogEvent('Campaign Builder - Install Template', {
				campaignID: campaign?.id,
				editingMethod,
			});
		}
		utils.reload(undefined, 350);
		return !!success;
	};

	const handleRejectProposal = async () => {
		await rejectProposal();
		setRejectProposalOpen(false);

		capturePosthogEvent('Campaign Builder - Reject Proposal', {
			campaignID: campaign?.id,
			editingMethod,
		});
	};

	const handleRetractRequest = async () => {
		const success = await cancelEditRequest();
		if (success) {
			setRetractRequestOpen(false);

			capturePosthogEvent('Campaign Builder - Retract Request', {
				campaignID: campaign?.id,
				editingMethod,
			});
		}
		return success;
	};

	const handleRequestEdits = async () => {
		const success = await requestEdits(comment);
		if (success) {
			setRequestEditsOpen(false);
			setComment('');

			capturePosthogEvent('Campaign Builder - Request Edits', {
				campaignID: campaign?.id,
				editingMethod,
			});
			utils.reload(undefined, 350);
		}
		return success;
	};

	const handleDenyEdits = async () => {
		const success = await denyEdits();
		if (success) {
			setRejectInstallOpen(false);
			setComment('');

			capturePosthogEvent('Campaign Builder - Deny Edits', {
				campaignID: campaign?.id,
				editingMethod,
			});
			// Until we can get the state to sync 100% we need to refresh the page
			utils.reload(undefined, 350);
		}
		return success;
	};

	const handleApproveEdits = async () => {
		const success = await approveEdits();
		if (success) {
			setApproveInstallOpen(false);
			setComment('');

			capturePosthogEvent('Campaign Builder - Approve Edits', {
				campaignID: campaign?.id,
				editingMethod,
			});

			// Until we can get the state to sync 100% we need to refresh the page
			utils.reload(undefined, 350);
		}
		return success;
	};

	const renderModals = () => {
		if (typeof document === 'undefined') return null;

		return createPortal(
			<>
				<AlertModal
					title="Install & Launch	Campaign"
					description="Are you sure you want to install this template and launch the campaign?"
					open={installTemplateOpen}
					onConfirm={handleInstallTemplate}
					onClose={() => setInstallTemplateOpen(false)}
				/>

				<AlertModal
					title="Request Edits"
					description={
						<>
							<FormTextArea
								label="Reason for edits"
								value={comment}
								onChange={({ target: { value } }) => setComment(value)}
								placeholder="Enter a comment for the sponsor"
								rows={3}
							/>
						</>
					}
					open={requestEditsForInstallOpen}
					onConfirm={handleRequestEditsForInstall}
					onClose={() => {
						setRequestEditsForInstallOpen(false);
						setComment('');
					}}
				/>

				<AlertModal
					title="Request Edits"
					description={
						<>
							<FormTextArea
								label="Reason for edits"
								value={comment}
								onChange={({ target: { value } }) => setComment(value)}
								placeholder="Enter a comment for the sponsor"
								rows={3}
							/>
						</>
					}
					open={requestEditsOpen}
					onConfirm={handleRequestEdits}
					onClose={() => {
						setRequestEditsOpen(false);
						setComment('');
					}}
				/>

				<AlertModal
					title="Launch campaign"
					description="Are you sure you want to launch this campaign?"
					open={launchCampaignOpen}
					onConfirm={handleLaunchCampaign}
					onClose={() => setLaunchCampaignOpen(false)}
				/>

				<AlertModal
					title="Deactivate campaign"
					description="Are you sure you want to deactivate this campaign?"
					open={deactivateCampaignOpen}
					onConfirm={handleDeactivateCampaign}
					onClose={() => setDeactivateCampaignOpen(false)}
				/>

				<AlertModal
					title="Send to Retailers"
					description="Are you sure you want to send this campaign to retailers?"
					open={sendToRetailersOpen}
					onConfirm={handleSendToRetailers}
					onClose={() => setSendToRetailersOpen(false)}
				/>

				<AlertModal
					title="Deactivate Co-Marketing"
					description="Are you sure you want to deactivate this co-marketing template?"
					open={deactivateCoMarketingOpen}
					onConfirm={handleDeactivateCoMarketing}
					onClose={() => setDeactivateCoMarketingOpen(false)}
				/>

				<AlertModal
					title="Reject Proposal"
					description="Are you sure you want to reject this proposal?"
					open={rejectProposalOpen}
					onConfirm={handleRejectProposal}
					onClose={() => {
						setRejectProposalOpen(false);
						setComment('');
					}}
				/>

				<AlertModal
					title="Retract Request"
					description="Are you sure you want to retract this request?"
					open={retractRequestOpen}
					onConfirm={handleRetractRequest}
					onClose={() => {
						setRetractRequestOpen(false);
						setComment('');
					}}
				/>

				<AlertModal
					title="Deny Edits"
					description="Are you sure you want to deny these edits?"
					open={rejectInstallOpen}
					onConfirm={handleDenyEdits}
					onClose={() => setRejectInstallOpen(false)}
				/>

				<AlertModal
					title="Approve Edits"
					description="Are you sure you want to approve these edits?"
					open={approveInstallOpen}
					onConfirm={handleApproveEdits}
					onClose={() => setApproveInstallOpen(false)}
				/>
			</>,
			document.body,
		);
	};

	// All possible combinations
	// - Co-Marketing
	// 	- Sponsor is viewing their own sponsored template (editing/creating)
	//    - Is campaign active?
	// 		- Yes:
	// 			- De-activate
	// 			- Save campaign
	// 		- No:
	// 			- Save as draft
	// 			- Launch campaign
	// 	- Sponsor is viewing an installed template (previewing)
	//    - Has made changes?
	// 		- Yes:
	// 			- Reject proposal
	// 			- Request edits
	// 		- No:
	// 			- Reject proposal
	// 			- Install template
	// 	- Installer is viewing their own installed template (editing/creating)
	//    - Is campaign active?
	// 		- Yes:
	// 			- De-activate
	//          - Requires Approval?
	// 				- Yes:
	// 					- Request Edits
	// 				- No:
	// 					- Save Campaign
	// 		- No:
	// 			- Requires Approval?
	// 				- Yes:
	// 					- Request Edits
	// 				- No:
	// 					- Launch campaign
	//
	// 	- Installer is viewing a sponsored template (previewing)
	// 	  - Has made changes?
	// 		- Yes:
	// 			- Reject proposal
	// 			- Request edits
	// 		- No:
	// 			- Reject proposal
	// 			- Install template
	// - No Co-Marketing
	// 	- Is campaign active?
	// 		- Yes:
	// 			- De-activate
	// 			- Save campaign
	// 		- No:
	// 			- Save as draft
	// 			- Launch campaign
	//
	// All unique actions:
	// - Reject proposal
	// - Request edits
	// - Install template
	// - Save as draft
	// - Launch campaign
	// - De-activate

	const actionButtons: React.ReactNode[] = [];

	// Installer is previewing the sponsored template
	if (editingMethod === CampaignEditingMethod.PREVIEW_SPONSORED) {
		actionButtons.push(
			<Button
				size="medium"
				onClick={() => {
					setRejectProposalOpen(true);
					capturePosthogEvent('Campaign Builder - Reject Proposal Prompt', {
						campaignID: campaign?.id,
						editingMethod,
					});
				}}
			>
				Reject Proposal
			</Button>,
		);

		actionButtons.push(
			<Button
				size="medium"
				variant="primary"
				onClick={() => {
					if (requiresApprovalForChanges) {
						setRequestEditsForInstallOpen(true);
						capturePosthogEvent('Campaign Builder - Request Edits for Install Prompt', {
							campaignID: campaign?.id,
							editingMethod,
						});
					} else {
						setInstallTemplateOpen(true);
						capturePosthogEvent('Campaign Builder - Install Template Prompt', {
							campaignID: campaign?.id,
							editingMethod,
						});
					}
				}}
			>
				{requiresApprovalForChanges ? 'Request Edits' : 'Launch Campaign'}
			</Button>,
		);
	}

	// Sponsor is previewing/editing one of their installed sponsored templates
	if (editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED) {
		actionButtons.push(
			<Button
				size="medium"
				disabled={!canSavePublish}
				variant="primary"
				onClick={() => {
					setRequestEditsOpen(true);
					capturePosthogEvent('Campaign Builder - Request Edits Prompt', {
						campaignID: campaign?.id,
						editingMethod,
					});
				}}
			>
				Request Edits
			</Button>,
		);
	}

	// Sponsor is editing their own sponsored template
	if (editingMethod === CampaignEditingMethod.EDITING_SPONSORING) {
		if (sponsoredTemplate?.published) {
			actionButtons.push(
				<Button
					size="medium"
					onClick={() => {
						setDeactivateCampaignOpen(true);
						capturePosthogEvent('Campaign Builder - Deactivate Campaign Prompt', {
							campaignID: campaign?.id,
							editingMethod,
						});
					}}
				>
					De-activate
				</Button>,
			);
		} else {
			actionButtons.push(
				<Button
					size="medium"
					disabled={!canSaveDraft}
					onClick={handleSave}
				>
					Save as Draft
				</Button>,
			);
		}
		actionButtons.push(
			<Button
				size="medium"
				disabled={!hasMadeChanges && !isCopying && sponsoredTemplate?.published}
				variant="primary"
				onClick={() => {
					if (sponsoredTemplate?.published) {
						handleSave();
					} else {
						setSendToRetailersOpen(true);
						capturePosthogEvent('Campaign Builder - Send to Retailers Prompt', {
							campaignID: campaign?.id,
							editingMethod,
						});
					}
				}}
			>
				{sponsoredTemplate?.published ? 'Save Template' : isCopying ? 'Copy & Send to Retailers' : 'Send to Retailers'}
			</Button>,
		);
	}

	// Installer is editing their own installed template
	if (editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED) {
		const hasInstallerRequest = !!installerRequest?.id;
		// If the campaign is active, we show the de-activate button
		if (campaign?.isActive) {
			actionButtons.push(
				<Button
					size="medium"
					onClick={() => {
						setDeactivateCampaignOpen(true);
						capturePosthogEvent('Campaign Builder - Deactivate Campaign Prompt', {
							campaignID: campaign?.id,
							editingMethod,
						});
					}}
				>
					De-activate
				</Button>,
			);
		} else {
			// Otherwise we show the save as draft button
			// Unless they have a pending request they are editing
			// biome-ignore lint/style/useCollapsedElseIf: <explanation>
			if (hasInstallerRequest) {
				actionButtons.push(
					<Button
						size="medium"
						onClick={() => {
							setRetractRequestOpen(true);
							capturePosthogEvent('Campaign Builder - Retract Request Prompt', {
								campaignID: campaign?.id,
								editingMethod,
							});
						}}
					>
						Retract Request
					</Button>,
				);
			} else {
				actionButtons.push(
					<Button
						size="medium"
						disabled={!canSaveDraft}
						onClick={handleSave}
					>
						Save as Draft
					</Button>,
				);
			}
		}

		// If the campaign is active we show
		if (requiresApprovalForChanges || hasInstallerRequest) {
			actionButtons.push(
				<Button
					size="medium"
					variant="primary"
					disabled={!canSaveDraft}
					onClick={() => {
						setRequestEditsOpen(true);
						capturePosthogEvent('Campaign Builder - Request Edits Prompt', {
							campaignID: campaign?.id,
							editingMethod,
						});
					}}
				>
					Request Edits
				</Button>,
			);
		} else {
			actionButtons.push(
				<Button
					size="medium"
					variant="primary"
					disabled={!canSavePublish && !campaign?.isActive}
					onClick={() => {
						if (campaign?.isActive) {
							handleSave();
						} else {
							setLaunchCampaignOpen(true);
							capturePosthogEvent('Campaign Builder - Launch Campaign Prompt', {
								campaignID: campaign?.id,
								editingMethod,
							});
						}
					}}
				>
					{campaign?.isActive ? 'Save Campaign' : isCopying ? 'Copy & Launch Campaign' : 'Launch Campaign'}
				</Button>,
			);
		}
	}

	if (
		editingMethod === CampaignEditingMethod.REVIEW_INSTALLER_CHANGES || // Installer is reviewing sponsor changes
		editingMethod === CampaignEditingMethod.REVIEW_SPONSOR_CHANGES // Sponsor is reviewing installer changes
	) {
		actionButtons.push(
			<Button
				size="medium"
				onClick={() => {
					setRejectInstallOpen(true);
					capturePosthogEvent('Campaign Builder - Deny Edits Prompt', {
						campaignID: campaign?.id,
						editingMethod,
					});
				}}
			>
				Deny Edits
			</Button>,
		);
		actionButtons.push(
			<Button
				size="medium"
				variant="primary"
				onClick={() => {
					setApproveInstallOpen(true);
					capturePosthogEvent('Campaign Builder - Approve Edits Prompt', {
						campaignID: campaign?.id,
						editingMethod,
					});
				}}
			>
				Approve Edits
			</Button>,
		);
	}

	// Standard campaign editing flow (no co-marketing)
	if (editingMethod === CampaignEditingMethod.DEFAULT) {
		if (campaign?.isActive) {
			actionButtons.push(
				<Button
					size="medium"
					onClick={() => {
						setDeactivateCampaignOpen(true);
						capturePosthogEvent('Campaign Builder - Deactivate Campaign Prompt', {
							campaignID: campaign?.id,
							editingMethod,
						});
					}}
				>
					De-activate
				</Button>,
			);
		} else {
			actionButtons.push(
				<Button
					size="medium"
					disabled={!canSaveDraft}
					onClick={handleSave}
				>
					Save as Draft
				</Button>,
			);
		}

		actionButtons.push(
			<Button
				size="medium"
				disabled={!canSavePublish}
				variant="primary"
				onClick={() => {
					if (campaign?.isActive) {
						handleSave();
					} else {
						setLaunchCampaignOpen(true);
						capturePosthogEvent('Campaign Builder - Launch Campaign Prompt', {
							campaignID: campaign?.id,
							editingMethod,
						});
					}
				}}
			>
				{campaign?.isActive ? 'Save Campaign' : isCopying ? 'Copy & Launch Campaign' : 'Launch Campaign'}
			</Button>,
		);
	}

	React.useEffect(() => {
		log('action buttons editingMethod', editingMethod);
	}, [editingMethod]);

	return (
		<>
			{renderModals() as React.ReactNode}

			<Flex align="center">{actionButtons}</Flex>
		</>
	);
}
